import { useMemo, useEffect } from 'react';
import { DateTime, Duration } from 'luxon';
import {
  useForm,
  useFieldArray,
  UseFormReturn,
  useWatch
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TimeSignal, LabelType } from 'graphql/types';
import { useCollatedTimesheetLabels } from 'modules/timesheet-labels/hooks';
import { useFeatureFlag, useLabels } from 'modules/common/hooks';
import {
  NOTE_FIELDS,
  TimesheetLabelFields,
  noteSchema
} from 'modules/notes/common/noteSchema';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useNoteFormDefaultValues } from 'modules/notes/addNote/hooks';
import { mergeResetLabels } from 'modules/notes/common/utils';
import { useCollatedCustomLabels } from 'modules/customLabels/hooks/useCollatedCustomLabels';
import { useFormErrorMessage } from 'modules/notes/hooks';

export interface UseGroupEditFormResultProps {
  formMethods: UseFormReturn;
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined;
  timesheetLabelFields: TimesheetLabelFields;
  labelsLoading: boolean;
  isValidForm: boolean;
  isFormDirty: boolean;
}

const useInitialStartAndEndTime = (notes: TimeSignal[]) =>
  useMemo(() => {
    const ascendingStartAndEndNotes = notes.filter(
      (note) => note.startTime && note.endTime
    );
    const start = ascendingStartAndEndNotes[0]?.startTime
      ? DateTime.fromISO(ascendingStartAndEndNotes[0].startTime)
      : null;

    const noteWithLatestEndTime = ascendingStartAndEndNotes.length
      ? ascendingStartAndEndNotes.reduce((a, b) =>
          a.endTime > b.endTime ? a : b
        )
      : null;

    const end = noteWithLatestEndTime?.endTime
      ? DateTime.fromISO(noteWithLatestEndTime.endTime)
      : null;

    const duration =
      start && end
        ? end.diff(start, ['hours', 'minutes', 'seconds'])
        : Duration.fromObject({ hours: 0, minutes: 0, seconds: 0 });

    const computedDuration = ascendingStartAndEndNotes.length
      ? duration
      : Duration.fromObject({
          seconds: notes.reduce((acc, note) => (acc += note.duration ?? 0), 0)
        });
    const durationInSec = computedDuration.shiftTo('seconds').toObject()
      .seconds;

    return {
      startTime: start ? start.toJSDate() : null,
      endTime: end ? end.toJSDate() : null,
      duration: durationInSec
    };
  }, [notes]);

const noteResolver = yupResolver(noteSchema(false));
const noteResolverNew = yupResolver(noteSchema(true));
export const useGroupEditForm = (
  notes: TimeSignal[],
  primaryNote: TimeSignal
): UseGroupEditFormResultProps => {
  const initialStartAndEndTime = useInitialStartAndEndTime(notes);
  const noteDate = useMemo(() => DateTime.fromISO(primaryNote.date), [
    primaryNote
  ]);

  const { timesheetLabels, loading: labelsLoading, customLabels } = useLabels(
    noteDate
  );
  const collatedTimesheetLabels = useCollatedTimesheetLabels(
    primaryNote,
    timesheetLabels
  );
  const collatedCustomLabels = useCollatedCustomLabels(
    primaryNote,
    customLabels
  );

  const transformedTimesheetLabels = useMemo(
    () =>
      collatedTimesheetLabels.map((label) =>
        label.type === LabelType.Comment
          ? {
              ...label,
              value: {
                stringValue: primaryNote.description
              }
            }
          : label
      ),
    [primaryNote, collatedTimesheetLabels]
  );

  const initialValues = useMemo(
    () => ({
      id: primaryNote.id,
      note: primaryNote.description,
      date: noteDate.toJSDate(),
      ...initialStartAndEndTime
    }),
    [primaryNote.id, primaryNote.description, noteDate, initialStartAndEndTime]
  );

  const defaultValues = useNoteFormDefaultValues({
    initialValues,
    timesheetLabels: transformedTimesheetLabels,
    customLabels: collatedCustomLabels,
    sources: primaryNote.sources
  });
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const formMethods = useForm({
    defaultValues,
    resolver: isTiwDurationIndependentStartEndTime
      ? noteResolverNew
      : noteResolver,
    shouldUnregister: false,
    mode: 'onChange'
  });

  const {
    control,
    getValues,
    reset,
    formState,
    setValue,
    trigger
  } = formMethods;

  const startTime = useWatch({
    control,
    name: 'startTime',
    defaultValue: defaultValues.startTime
  });

  const endTime = useWatch({
    control,
    name: 'endTime',
    defaultValue: defaultValues.endTime
  });

  useEffect(() => {
    if (startTime && endTime) {
      const start = DateTime.fromJSDate(startTime);
      const end = DateTime.fromJSDate(endTime);
      if (start <= end) {
        const duration = end.diff(start, ['hours', 'minutes', 'seconds']);
        const durationInSec = duration.shiftTo('seconds').toObject().seconds;
        setValue(
          `${NOTE_FIELDS.DURATION}`,
          durationInSec ? Math.round(durationInSec) : 0
        );
      } else {
        setValue(`${NOTE_FIELDS.DURATION}`, 0);
      }
      trigger();
    }
  }, [endTime, startTime, setValue, trigger]);

  useEffect(() => {
    // if timesheet labels popuplated/changed after initial values (e.g. date changed)
    const values = getValues();
    const resetLabels = mergeResetLabels(defaultValues, values);
    if (resetLabels) {
      reset(
        { ...values, [NOTE_FIELDS.TIMESHEET_LABELS]: resetLabels },
        {
          keepErrors: true, // errors will not be reset
          keepDirty: true // dirtyFields will not be reset
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.timesheetLabels]);

  const { fields: timesheetFields } = useFieldArray({
    control,
    name: NOTE_FIELDS.TIMESHEET_LABELS,
    keyName: 'key'
  });

  const { isDirty, isValid, dirtyFields } = formState;

  const errorMessage = useFormErrorMessage(formState);

  const isFormDirty = useMemo(() => {
    return isDirty && !!Object.keys(dirtyFields).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(dirtyFields).length, isDirty]);

  const isValidForm = useMemo(() => isFormDirty && isValid, [
    isFormDirty,
    isValid
  ]);

  return {
    formMethods,
    errorMessage,
    timesheetLabelFields: (timesheetFields as unknown) as Record<
      'key',
      TimesheetLabel
    >[],
    labelsLoading,
    isValidForm,
    isFormDirty
  };
};
